import React, { useState, useCallback, useRef } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { EmailDesign, EmailBlock } from './types';
import { ComponentLibrary } from './components/ComponentLibrary';
import { EmailCanvas } from './components/EmailCanvas';
import { PropertiesPanel } from './components/PropertiesPanel';
import { createBlock, updateBlock, removeBlock, cloneBlock } from './utils/blockSerializer';

interface EmailBuilderProps {
  initialDesign?: EmailDesign;
  onDesignChange?: (design: EmailDesign) => void;
  onExportHtml?: (html: string) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
}

export const EmailBuilder: React.FC<EmailBuilderProps> = ({
  initialDesign,
  onDesignChange,
  onExportHtml,
  mergeTags
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme.mode === 'dark' || 
    (theme.mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
    document.documentElement.classList.contains('dark');

  const [design, setDesign] = useState<EmailDesign>(
    initialDesign || { blocks: [], styles: { backgroundColor: '#ffffff', width: '600px', maxWidth: '600px' } }
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [draggedBlockType, setDraggedBlockType] = useState<EmailBlock['type'] | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle design changes
  const handleDesignChange = useCallback((newDesign: EmailDesign) => {
    setDesign(newDesign);
    onDesignChange?.(newDesign);
  }, [onDesignChange]);

  // Handle drag start from component library
  const handleDragStart = useCallback((blockType: EmailBlock['type']) => {
    setDraggedBlockType(blockType);
    setIsDragging(true);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedBlockType(null);
  }, []);

  // Handle drop on canvas
  const handleDrop = useCallback((index?: number) => {
    if (!draggedBlockType) return;

    const newBlock = createBlock(draggedBlockType);
    const newBlocks = [...design.blocks];
    
    if (index !== undefined) {
      newBlocks.splice(index, 0, newBlock);
    } else {
      newBlocks.push(newBlock);
    }

    const newDesign = { ...design, blocks: newBlocks };
    handleDesignChange(newDesign);
    setSelectedBlockId(newBlock.id);
    handleDragEnd();
  }, [draggedBlockType, design, handleDesignChange, handleDragEnd]);

  // Handle block selection
  const handleBlockSelect = useCallback((blockId: string) => {
    setSelectedBlockId(blockId);
  }, []);

  // Handle block update
  const handleBlockUpdate = useCallback((blockId: string, updates: Partial<EmailBlock>) => {
    const newDesign = updateBlock(design, blockId, updates);
    handleDesignChange(newDesign);
  }, [design, handleDesignChange]);

  // Handle block delete
  const handleBlockDelete = useCallback((blockId: string) => {
    const newDesign = removeBlock(design, blockId);
    handleDesignChange(newDesign);
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }, [design, handleDesignChange, selectedBlockId]);

  // Handle block duplicate
  const handleBlockDuplicate = useCallback((blockId: string) => {
    const block = design.blocks.find(b => b.id === blockId);
    if (!block) return;

    const clonedBlock = cloneBlock(block);
    const blockIndex = design.blocks.findIndex(b => b.id === blockId);
    const newBlocks = [...design.blocks];
    newBlocks.splice(blockIndex + 1, 0, clonedBlock);
    
    const newDesign = { ...design, blocks: newBlocks };
    handleDesignChange(newDesign);
    setSelectedBlockId(clonedBlock.id);
  }, [design, handleDesignChange]);

  // Get selected block
  const selectedBlock = selectedBlockId 
    ? design.blocks.find(b => b.id === selectedBlockId) || null
    : null;

  return (
    <div className="flex h-full w-full bg-gray-50 dark:bg-gray-900">
      {/* Component Library Sidebar */}
      <div className="w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
        <ComponentLibrary
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Canvas Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          <EmailCanvas
            design={design}
            selectedBlockId={selectedBlockId}
            onBlockSelect={handleBlockSelect}
            onBlockUpdate={handleBlockUpdate}
            onBlockDelete={handleBlockDelete}
            onBlockDuplicate={handleBlockDuplicate}
            onDrop={handleDrop}
            isDragging={isDragging}
            draggedBlockType={draggedBlockType}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>

      {/* Properties Panel */}
      {selectedBlock && (
        <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
          <PropertiesPanel
            block={selectedBlock}
            onUpdate={(updates) => handleBlockUpdate(selectedBlock.id, updates)}
            mergeTags={mergeTags}
            isDarkMode={isDarkMode}
          />
        </div>
      )}
    </div>
  );
};
